import React from "react";
import EmptyState from "../common/empty_state";
import BollingerGauge from "./bollinger_gauge";
import BandScenarioPnl from "./band_scenario_pnl";
import LoanPanel from "./loan_panel";
import { PRIORITY_TO_STATUS_KEY } from "../common/filter_bar";

const formatNumberWithCommaAndDecimal = (number, decimalPlaces = 3) => {
    if (isNaN(number)) {
        return "";
    }
    return Number(number).toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });
};

const formatPercent = (value, decimalPlaces = 2) => {
    if (!Number.isFinite(value)) {
        return "-";
    }
    const sign = value > 0 ? "+" : "";
    return `${sign}${value.toFixed(decimalPlaces)}%`;
};

/* DAS-83: 시안 PositionCard 포맷 헬퍼 (ClaudeDesignDashboard/pages/dashboard.jsx::krw0/krwS/usdS/pctS 정합) */
const krw0Signed = (n) => {
    if (!Number.isFinite(n)) return "—";
    return (n < 0 ? "-" : "") + "₩" + Math.abs(Math.round(n)).toLocaleString();
};
const krwSigned = (n) => {
    if (!Number.isFinite(n)) return "—";
    const s = n >= 0 ? "+" : "-";
    return s + "₩" + Math.abs(Math.round(n)).toLocaleString();
};
const pctSigned = (n) => {
    if (!Number.isFinite(n)) return "—";
    return (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
};
const usdSigned = (n) => {
    if (!Number.isFinite(n)) return "—";
    const s = n >= 0 ? "+" : "-";
    return s + "$" + Math.abs(n).toFixed(2);
};

/* DAS-83: 시안 PositionCard tone 매핑 — priority(0~5) → 시안 STATUSES tone */
const PRIORITY_TO_SCHEME = {
    0: { tone: "loss",    label: "청산위험" },
    1: { tone: "warning", label: "고위험" },
    2: { tone: "warning", label: "주의" },
    3: { tone: "brand",   label: "관찰" },
    4: { tone: "gain",    label: "안정" },
    5: { tone: "neutral", label: "정보없음" },
};

const toNumberOrNaN = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const getFiatCurrentPrice = (position) => {
    const currentPrice = toNumberOrNaN(position?.current_price);
    return currentPrice > 0 ? currentPrice : Number.NaN;
};

const getRiskyCurrentPrice = (position) => {
    const currentPrice = toNumberOrNaN(position?.current_price ?? position?.currentPrice);
    if (currentPrice > 0) {
        return currentPrice;
    }

    const markPrice = toNumberOrNaN(
        position?.markPrice ?? position?.mark_price ?? position?.lastPrice ?? position?.last_price
    );
    if (markPrice > 0) {
        return markPrice;
    }

    const entryPrice = toNumberOrNaN(position?.entryPrice);
    const positionAmt = toNumberOrNaN(position?.positionAmt);
    const unrealizedProfit = toNumberOrNaN(position?.unrealizedProfit);
    if (entryPrice > 0 && Number.isFinite(positionAmt) && positionAmt !== 0 && Number.isFinite(unrealizedProfit)) {
        const inferredPrice = entryPrice + unrealizedProfit / positionAmt;
        return inferredPrice > 0 ? inferredPrice : Number.NaN;
    }

    return Number.NaN;
};

const getRiskLevel = (pnlPercent) => {
    // DAS-73 Phase 3 (D1): rowBgClass — 시안 PositionCard borderLeft 3px tone 색상을
    //   row 배경 미묘 subtle 톤으로 매핑 (디자인 시스템 토큰만 사용, WCAG AA 유지).
    //   priority 0~3 만 적용 (안정/정보없음은 기본 surface-elevated 유지 — 시각 노이즈 방지).
    if (!Number.isFinite(pnlPercent)) {
        return { label: "정보없음", className: "bg-surface-hover text-content-secondary", borderClass: "border-l-surface-border", rowBgClass: "bg-surface-elevated", priority: 5 };
    }
    if (pnlPercent <= -80) {
        return { label: "청산위험", className: "bg-loss-dark text-white", borderClass: "border-l-loss-dark", rowBgClass: "bg-loss-subtle", priority: 0 };
    }
    if (pnlPercent <= -60) {
        return { label: "고위험", className: "bg-loss text-white", borderClass: "border-l-loss", rowBgClass: "bg-loss-subtle", priority: 1 };
    }
    if (pnlPercent <= -40) {
        return { label: "주의", className: "bg-warning text-content-inverse", borderClass: "border-l-warning", rowBgClass: "bg-warning-subtle", priority: 2 };
    }
    if (pnlPercent <= -20) {
        return { label: "관찰", className: "bg-warning-light text-content-inverse", borderClass: "border-l-warning-light", rowBgClass: "bg-warning-subtle", priority: 3 };
    }
    return { label: "안정", className: "bg-surface-hover text-content", borderClass: "border-l-surface-border-light", rowBgClass: "bg-surface-elevated", priority: 4 };
};

class PositionTable extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            searchText: "",
            sortKey: "risk_priority",
            sortDir: "asc",
            hideSmallPositions: true,
            expandedTickers: new Set(),
        };
    }

    convertToKRW = (amount, exchangeRate, decimalPlaces = 3) => {
        return (amount * exchangeRate).toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        });
    };

    handleSort = (key) => {
        this.setState((prev) => {
            if (prev.sortKey === key) {
                return { sortKey: key, sortDir: prev.sortDir === "desc" ? "asc" : "desc" };
            }
            return { sortKey: key, sortDir: "desc" };
        });
    };

    toggleExpand = (ticker) => {
        this.setState((prev) => {
            const next = new Set(prev.expandedTickers);
            if (next.has(ticker)) next.delete(ticker);
            else next.add(ticker);
            return { expandedTickers: next };
        });
    };

    toggleAllExpand = (allTickers) => {
        const { expandedTickers } = this.state;
        const anyCollapsed = allTickers.some((t) => !expandedTickers.has(t));
        if (anyCollapsed) {
            this.setState({ expandedTickers: new Set(allTickers) });
        } else {
            this.setState({ expandedTickers: new Set() });
        }
    };

    renderPnlIcon = (value) => {
        if (!Number.isFinite(value) || value === 0) return null;
        return (
            <svg
                className={`inline-block h-2.5 w-2.5 mr-0.5${value < 0 ? " rotate-180" : ""}`}
                viewBox="0 0 12 12"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M6 2l4 5H2z" />
            </svg>
        );
    };

    render() {
        const {
            exchangeRate, exchangeRateSource, fiatPositions, riskyPositions, bollinger, riskyMarkPrices,
            excludeBTC, onToggleExcludeBTC,
            // DAS-182: 업비트 코인빌리기(담보·대여) 잔차 추정 블록. loan 이 없으면(추정 시작 전) 렌더하지 않는다.
            loan, username, onLoanChanged,
            // DAS-78 Phase 3.5: FilterBar 상태 칩 필터 — 빈 Set/null 이면 전체 통과.
            //   외부 페이지 레벨에서 selectedStatuses 를 주입한다.
            selectedStatuses,
            // DAS-167: 위험 그룹별로 PositionTable 을 반복 렌더하는 화면에서는 기본값(true)대로 두면
            //   전체 포지션 기준 시나리오 카드가 그룹 수만큼 중복 표시된다. 그룹 반복 렌더 경로에서만
            //   false 로 꺼서 페이지 레벨에 단일 배치하거나 생략한다.
            showScenarioPnl = true,
        } = this.props;
        const { searchText, sortKey, sortDir, hideSmallPositions, expandedTickers } = this.state;

        const fiatPositionsArray = Array.isArray(fiatPositions) ? fiatPositions : [];
        const riskyPositionsArray = Array.isArray(riskyPositions) ? riskyPositions : [];
        const getRiskyMarkPrice = (ticker) => {
            if (!riskyMarkPrices) return Number.NaN;
            const symbolKey = `${ticker.toUpperCase()}USDT`;
            const direct = Number(riskyMarkPrices[symbolKey]);
            if (Number.isFinite(direct) && direct > 0) {
                return direct;
            }
            const alt = Number(riskyMarkPrices[ticker.toUpperCase()]);
            return Number.isFinite(alt) && alt > 0 ? alt : Number.NaN;
        };

        const combinedData = {};

        fiatPositionsArray.forEach((position) => {
            const { currency, avg_buy_price, balance, profit_loss_amount } = position;
            const ticker = currency.toUpperCase();
            const parsedBalance = Number(balance) || 0;
            const parsedAvgBuyPrice = parseFloat(avg_buy_price) || 0;
            const parsedProfitLoss = Number(profit_loss_amount) || 0;
            const positionValue = parsedBalance * parsedAvgBuyPrice;
            const isCollateral = Boolean(position.in_collateral);
            const isAvgOverridden = Boolean(position.avg_price_overridden);
            const exchangeAvgBuyPrice = parseFloat(position.exchange_avg_buy_price) || 0;

            if (!combinedData[ticker]) {
                combinedData[ticker] = {
                    ticker: ticker,
                    fiat_avg_buy_price: parsedAvgBuyPrice,
                    fiat_current_price: getFiatCurrentPrice(position),
                    fiat_position_size: parsedBalance,
                    fiat_position_value: positionValue,
                    fiat_profit_loss_amount: parsedProfitLoss,
                    risky_avg_buy_price: 0,
                    risky_current_price: Number.NaN,
                    risky_position_size: 0,
                    risky_position_value: 0,
                    risky_profit_loss_amount: 0,
                    risky_leverage: 1,
                    risky_profit_loss_percent: Number.NaN,
                    // DAS-182 계약 보완: 담보로 이체된 코인을 복원한 유사 포지션 표시용 플래그.
                    // collateral_qty는 "담보" 배지의 수량 안내(title)용 — 병합 시 담보분만 누적한다.
                    in_collateral: isCollateral,
                    collateral_qty: isCollateral ? parsedBalance : 0,
                    // DAS-182 계약 보완: 업비트 담보 반환 시 평단이 시세로 재설정되는 문제를 서버가 보정한
                    // 경우의 표시용 플래그 — exchange_avg_buy_price는 보정 전(업비트 원래) 평단.
                    avg_price_overridden: isAvgOverridden,
                    exchange_avg_buy_price: isAvgOverridden ? exchangeAvgBuyPrice : 0,
                };
            } else {
                // MAJOR-6: 같은 티커에 계좌 보유분 + 담보 복원분이 별도 항목으로 올 수 있다(부분 담보) — 합산한다.
                const existing = combinedData[ticker];
                const mergedSize = existing.fiat_position_size + parsedBalance;
                const mergedValue = existing.fiat_position_value + positionValue;
                existing.fiat_position_size = mergedSize;
                existing.fiat_position_value = mergedValue;
                existing.fiat_avg_buy_price = mergedSize > 0 ? mergedValue / mergedSize : existing.fiat_avg_buy_price;
                existing.fiat_profit_loss_amount += parsedProfitLoss;
                if (!Number.isFinite(existing.fiat_current_price)) {
                    existing.fiat_current_price = getFiatCurrentPrice(position);
                }
                if (isCollateral) {
                    existing.in_collateral = true;
                    existing.collateral_qty = (existing.collateral_qty || 0) + parsedBalance;
                }
                if (isAvgOverridden) {
                    existing.avg_price_overridden = true;
                    existing.exchange_avg_buy_price = existing.exchange_avg_buy_price || exchangeAvgBuyPrice;
                }
            }
        });

        riskyPositionsArray.forEach((position) => {
            const { symbol, entryPrice, positionAmt, unrealizedProfit, leverage } = position;
            const ticker = symbol.replace("USDT", "");
            const parsedLeverage = Number(leverage);

            if (!combinedData[ticker]) {
                combinedData[ticker] = {
                    ticker: ticker.toUpperCase(),
                    fiat_avg_buy_price: 0,
                    fiat_current_price: Number.NaN,
                    fiat_position_size: 0,
                    fiat_position_value: 0,
                    fiat_profit_loss_amount: 0,
                };
            }
            combinedData[ticker].risky_avg_buy_price = parseFloat(entryPrice) || 0;
            combinedData[ticker].risky_current_price = getRiskyCurrentPrice(position);
            combinedData[ticker].risky_position_size = parseFloat(positionAmt) || 0;
            combinedData[ticker].risky_position_value = positionAmt * entryPrice || 0;
            combinedData[ticker].risky_profit_loss_amount = parseFloat(unrealizedProfit) || 0;
            combinedData[ticker].risky_leverage =
                Number.isFinite(parsedLeverage) && parsedLeverage > 0 ? parsedLeverage : 1;
        });

        Object.keys(combinedData).forEach((ticker) => {
            if (!Number.isFinite(combinedData[ticker].risky_current_price)) {
                const markPrice = getRiskyMarkPrice(ticker);
                if (Number.isFinite(markPrice)) {
                    combinedData[ticker].risky_current_price = markPrice;
                }
            }
            const spotPrice = combinedData[ticker].fiat_current_price;
            const futuresPrice = combinedData[ticker].risky_current_price;
            combinedData[ticker].current_ratio =
                Number.isFinite(spotPrice) && Number.isFinite(futuresPrice) && spotPrice > 0 && futuresPrice > 0
                    ? spotPrice / futuresPrice
                    : Number.NaN;
            const entrySpotPrice = combinedData[ticker].fiat_avg_buy_price;
            const entryFuturesPrice = combinedData[ticker].risky_avg_buy_price;
            combinedData[ticker].entry_usdt_rate =
                Number.isFinite(entrySpotPrice) &&
                Number.isFinite(entryFuturesPrice) &&
                entrySpotPrice > 0 &&
                entryFuturesPrice > 0
                    ? entrySpotPrice / entryFuturesPrice
                    : Number.NaN;
            combinedData[ticker].total_profit_loss_amount =
                combinedData[ticker].risky_profit_loss_amount * exchangeRate +
                combinedData[ticker].fiat_profit_loss_amount;
            const denominator =
                combinedData[ticker].fiat_position_value +
                Math.abs(combinedData[ticker].risky_position_value * exchangeRate);
            combinedData[ticker].total_profit_loss_ratio =
                denominator > 0
                    ? combinedData[ticker].total_profit_loss_amount / denominator
                    : null;
            combinedData[ticker].total_position_value =
                combinedData[ticker].fiat_position_value +
                Math.abs(combinedData[ticker].risky_position_value * exchangeRate);

            const riskyNotional = combinedData[ticker].risky_position_value;
            const riskyLeverage = combinedData[ticker].risky_leverage;
            const riskyMargin =
                Math.abs(riskyNotional) > 0 && Number.isFinite(riskyLeverage) && riskyLeverage > 0
                    ? Math.abs(riskyNotional) / riskyLeverage
                    : 0;
            combinedData[ticker].risky_profit_loss_percent =
                riskyMargin > 0
                    ? (combinedData[ticker].risky_profit_loss_amount / riskyMargin) * 100
                    : Number.NaN;
            combinedData[ticker].risk_priority = getRiskLevel(combinedData[ticker].risky_profit_loss_percent).priority;
        });

        let finalCombinedData = Object.values(combinedData);

        if (searchText) {
            const keyword = searchText.toLowerCase();
            finalCombinedData = finalCombinedData.filter((row) =>
                row.ticker.toLowerCase().includes(keyword)
            );
        }

        if (hideSmallPositions) {
            finalCombinedData = finalCombinedData.filter((row) => row.total_position_value > 10000);
        }

        // DAS-78 Phase 3.5: 위험 상태 칩 필터 (priority → status key 매핑 후 selectedStatuses 비교).
        //   selectedStatuses 가 Set 이고 size > 0 일 때만 적용. 빈 Set/null/undefined 는 "전체" 의미.
        if (selectedStatuses instanceof Set && selectedStatuses.size > 0) {
            finalCombinedData = finalCombinedData.filter((row) => {
                const statusKey = PRIORITY_TO_STATUS_KEY[row.risk_priority];
                return statusKey ? selectedStatuses.has(statusKey) : false;
            });
        }

        finalCombinedData = finalCombinedData.sort((a, b) => {
            const aValue = a[sortKey] ?? 0;
            const bValue = b[sortKey] ?? 0;
            const primary = sortDir === "desc" ? bValue - aValue : aValue - bValue;
            if (primary !== 0) return primary;
            return (b.total_position_value ?? 0) - (a.total_position_value ?? 0);
        });

        const allTickers = finalCombinedData.map((r) => r.ticker);
        const anyCollapsed = allTickers.some((t) => !expandedTickers.has(t));

        return (
            <div className="w-full rounded-2xl border border-surface-border bg-surface-card p-3 shadow-card sm:p-4">
                {/* 헤더: 제목 + 필터/정렬 2단 구성 */}
                <div className="space-y-2.5">
                    {/* 1단: 제목 + 토글 필터 */}
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-content-secondary">
                            포지션 정보 <span className="font-normal text-content-muted">({finalCombinedData.length})</span>
                        </h3>
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => this.setState((prev) => ({ hideSmallPositions: !prev.hideSmallPositions }))}
                                aria-label="1만이하 숨김"
                                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                                    hideSmallPositions
                                        ? "border-brand bg-brand-subtle text-brand-light"
                                        : "border-surface-border text-content-secondary hover:bg-surface-hover"
                                }`}
                            >
                                ₩1만↓ 숨김
                            </button>
                            {onToggleExcludeBTC && (
                                <button
                                    type="button"
                                    onClick={onToggleExcludeBTC}
                                    aria-label="BTC 제외"
                                    className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                                        excludeBTC
                                            ? "border-warning bg-warning-subtle text-warning"
                                            : "border-surface-border text-content-secondary hover:bg-surface-hover"
                                    }`}
                                    title={excludeBTC ? "BTC 포지션이 제외된 상태입니다" : "BTC 포지션을 미실현 손익 계산에서 제외합니다"}
                                >
                                    BTC 제외
                                </button>
                            )}
                        </div>
                    </div>
                    {/* 2단: 정렬 + 검색 */}
                    <div className="flex items-center gap-2">
                        <select
                            value={`${sortKey}_${sortDir}`}
                            onChange={(e) => {
                                const val = e.target.value;
                                const lastUnderscore = val.lastIndexOf("_");
                                const key = val.substring(0, lastUnderscore);
                                const dir = val.substring(lastUnderscore + 1);
                                this.setState({ sortKey: key, sortDir: dir });
                            }}
                            aria-label="정렬 기준"
                            className="shrink-0 rounded-lg border border-surface-border bg-surface-elevated px-2 py-1.5 text-xs text-content-secondary focus:border-brand focus:outline-none"
                        >
                            <option value="risk_priority_asc">위험도 높은순</option>
                            <option value="risk_priority_desc">위험도 낮은순</option>
                            <option value="total_position_value_desc">총 가치 ↓</option>
                            <option value="total_position_value_asc">총 가치 ↑</option>
                            <option value="total_profit_loss_amount_desc">손익금 ↓</option>
                            <option value="total_profit_loss_amount_asc">손익금 ↑</option>
                        </select>
                        <input
                            type="text"
                            placeholder="Ticker 검색"
                            value={searchText}
                            onChange={(event) => this.setState({ searchText: event.target.value })}
                            aria-label="티커 검색"
                            className="flex-1 min-w-0 rounded-lg border border-surface-border bg-surface-elevated px-3 py-1.5 text-sm text-content placeholder-content-muted focus:border-brand focus:outline-none"
                        />
                    </div>
                </div>

                <div className="min-h-20">
                {bollinger && (Number.isFinite(bollinger.middle) || Number.isFinite(bollinger.high) || Number.isFinite(bollinger.low)) && (
                <div className="sticky top-12 z-10 -mx-3 border-b border-surface-border bg-surface-card/95 px-3 pb-2 shadow-card backdrop-blur-sm animate-fade-in">
                    <div className="mt-3">
                        <BollingerGauge
                            low={bollinger?.low}
                            middle={bollinger?.middle}
                            high={bollinger?.high}
                            currentRatio={exchangeRate}
                            exchangeRate={exchangeRate}
                            exchangeRateSource={exchangeRateSource}
                            stale={bollinger?.stale}
                            computedAtMs={bollinger?.computed_at_ms}
                        />
                    </div>
                </div>
                )}
                </div>

                {/* DAS-182: 코인빌리기(담보·대여) 잔차 추정 블록 — 게이지 아래, 밴드 시나리오 카드 위.
                    loan 이 없으면(추정 시작 전) LoanPanel 내부에서도 방어적으로 null을 반환한다. */}
                {loan && (
                    <LoanPanel loan={loan} username={username} onChanged={onLoanChanged} />
                )}

                {/* DAS-167: 밴드 상/하단 도달 시 예상 총 손익 — sticky 블록 밖, 렌더 여부는 컴포넌트 내부에서 ready/hasPosition 으로 자체 게이팅.
                    showScenarioPnl=false (v3 위험 그룹별 반복 렌더 경로)면 중복 카드 방지를 위해 생략한다. */}
                {showScenarioPnl && (
                    <BandScenarioPnl
                        bollinger={bollinger}
                        exchangeRate={exchangeRate}
                        fiatPositions={fiatPositions}
                        riskyPositions={riskyPositions}
                        riskyMarkPrices={riskyMarkPrices}
                    />
                )}

                {/* 위험 범례 + 모두 펼치기/접기 버튼 */}
                <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-1 text-xs font-semibold sm:gap-2">
                        {[
                            { label: "청산위험", className: "bg-loss-dark text-white" },
                            { label: "고위험", className: "bg-loss text-white" },
                            { label: "주의", className: "bg-warning text-content-inverse" },
                            { label: "관찰", className: "bg-warning-light text-content-inverse" },
                            { label: "안정", className: "bg-surface-hover text-content" },
                        ].map((item) => (
                            <span
                                key={item.label}
                                className={`rounded-full px-2 py-0.5 sm:px-3 sm:py-1 ${item.className}`}
                            >
                                {item.label}
                            </span>
                        ))}
                    </div>
                    {allTickers.length > 0 && (
                        <button
                            type="button"
                            onClick={() => this.toggleAllExpand(allTickers)}
                            className="shrink-0 rounded-lg border border-surface-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-hover transition-colors"
                        >
                            {anyCollapsed ? "모두 펼치기" : "모두 접기"}
                        </button>
                    )}
                </div>

                <div
                    className="mt-4 grid grid-cols-1 items-start gap-2 sm:grid-cols-2 sm:gap-3 xl:grid-cols-3 transition-all duration-300 [&_*]:min-w-0"
                    data-variant={this.props.variant === "designed" ? "designed" : "default"}
                >
                    {/* DAS-83: variant="designed" 분기 — 시안 PositionCard 포맷 (ClaudeDesignDashboard/pages/dashboard.jsx L256~381).
                        v1 (variant 미전달) 은 기존 운영 카드 그대로 유지. */}
                    {this.props.variant === "designed" && finalCombinedData.map((record, index) => {
                        const risk = getRiskLevel(record.risky_profit_loss_percent);
                        const scheme = PRIORITY_TO_SCHEME[risk.priority] || PRIORITY_TO_SCHEME[5];
                        const tone = scheme.tone; // loss/warning/brand/gain/neutral
                        const pnl = Number(record.total_profit_loss_amount);
                        const pnlPct = Number.isFinite(record.total_profit_loss_ratio)
                            ? record.total_profit_loss_ratio * 100
                            : Number.NaN;
                        const value = Number(record.total_position_value);
                        const ratioDiff = Number.isFinite(record.current_ratio) && Number.isFinite(record.entry_usdt_rate)
                            ? record.current_ratio - record.entry_usdt_rate
                            : Number.NaN;
                        const ratioDiffPct = Number.isFinite(ratioDiff) && Number.isFinite(record.entry_usdt_rate) && record.entry_usdt_rate > 0
                            ? (ratioDiff / record.entry_usdt_rate) * 100
                            : Number.NaN;
                        const ratioToneClass = Number.isFinite(ratioDiff)
                            ? ratioDiff > 0 ? "text-gain" : ratioDiff < 0 ? "text-loss" : "text-content-muted"
                            : "text-content-muted";
                        const fiatPnl = Number(record.fiat_profit_loss_amount);
                        const fiatPnlPct = record.fiat_position_value
                            ? (fiatPnl / record.fiat_position_value) * 100
                            : Number.NaN;
                        const riskyPnl = Number(record.risky_profit_loss_amount);
                        const riskyPnlKrw = riskyPnl * exchangeRate;
                        const riskyPnlPct = Number(record.risky_profit_loss_percent);
                        const sizeDiff = record.fiat_position_size + record.risky_position_size;
                        const hasQtyDiff = Math.abs(sizeDiff) > 1e-9;
                        const leverage = Number.isFinite(record.risky_leverage) && record.risky_leverage > 1
                            ? record.risky_leverage
                            : null;

                        return (
                            <div
                                key={record.ticker}
                                className={`flex flex-col rounded-xl border border-surface-border bg-surface-card animate-fade-in${risk.priority === 0 ? ' animate-pulse-soft shadow-glow-loss' : ''}`}
                                style={{
                                    borderLeft: `3px solid var(--color-${tone === "neutral" ? "surface-border" : tone})`,
                                    animationDelay: `${Math.min(index * 40, 500)}ms`,
                                    animationFillMode: 'backwards',
                                }}
                            >
                                {/* Head: 티커 + 상태 배지 + 레버리지 */}
                                <div
                                    className="flex items-center justify-between gap-2 border-b border-surface-border px-3.5 py-3 min-h-[44px] cursor-pointer select-none"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => this.toggleExpand(record.ticker)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.toggleExpand(record.ticker); }}
                                    aria-label={`${record.ticker} 카드 펼치기/접기`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="font-semibold text-sm tracking-tight text-content">{record.ticker}</span>
                                        {record.in_collateral && (
                                            <span
                                                title={`코인빌리기 담보로 이체된 수량(추정): ${formatNumberWithCommaAndDecimal(record.collateral_qty, 4)} ${record.ticker}`}
                                                aria-label="코인빌리기 담보"
                                                className="rounded-full bg-brand-subtle px-1.5 py-0.5 text-xs font-semibold text-brand-light"
                                            >
                                                담보
                                            </span>
                                        )}
                                        {record.avg_price_overridden && (
                                            <span
                                                title={`업비트 평단 ${formatNumberWithCommaAndDecimal(record.exchange_avg_buy_price, 0)}원 → 보정 ${formatNumberWithCommaAndDecimal(record.fiat_avg_buy_price, 0)}원`}
                                                aria-label="평단 보정"
                                                className="rounded-full bg-brand-subtle px-1.5 py-0.5 text-xs font-semibold text-brand-light"
                                            >
                                                평단 보정
                                            </span>
                                        )}
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${risk.className}`}>
                                            {scheme.label}
                                        </span>
                                        {leverage && (
                                            <span className="font-num rounded-md border border-surface-border px-1.5 py-0.5 text-xs font-semibold text-content-muted">
                                                {leverage}x
                                            </span>
                                        )}
                                    </div>
                                    <svg
                                        className={`h-3.5 w-3.5 text-content-muted transition-transform duration-300 ${expandedTickers.has(record.ticker) || risk.priority === 0 ? "rotate-180" : ""}`}
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        aria-hidden="true"
                                    >
                                        <path d="M2 4l4 4 4-4" />
                                    </svg>
                                </div>

                                {/* 메인 메트릭 3열: 총 손익 / 총 가치 / Ratio */}
                                <div className="grid grid-cols-3 gap-2.5 border-b border-surface-border px-3.5 py-3">
                                    <div className="min-w-0">
                                        <div className="text-xs uppercase tracking-[0.14em] text-content-muted font-medium font-num">총 손익</div>
                                        <div className={`mt-0.5 truncate font-num text-sm font-semibold ${pnl >= 0 ? "text-gain" : "text-loss"}`}>
                                            {krwSigned(pnl)}
                                        </div>
                                        <div className={`mt-0.5 font-num text-xs ${Number.isFinite(pnlPct) ? (pnlPct >= 0 ? "text-gain" : "text-loss") : "text-content-muted"}`}>
                                            {pctSigned(pnlPct)}
                                        </div>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-xs uppercase tracking-[0.14em] text-content-muted font-medium font-num">총 가치</div>
                                        <div className="mt-0.5 truncate font-num text-sm font-semibold text-content">
                                            {krw0Signed(value)}
                                        </div>
                                        <div className="mt-0.5 font-num text-xs text-content-muted">
                                            {hasQtyDiff ? "Δ 불일치" : "정합"}
                                        </div>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-xs uppercase tracking-[0.14em] text-content-muted font-medium font-num">Ratio</div>
                                        <div className="mt-0.5 truncate font-num text-sm font-semibold leading-tight tracking-tight text-content">
                                            {Number.isFinite(record.current_ratio) ? record.current_ratio.toFixed(2) : "—"}
                                        </div>
                                        <div className="mt-0.5 flex items-baseline gap-1 flex-wrap">
                                            {Number.isFinite(ratioDiff) && (
                                                <>
                                                    <span className={`font-num text-xs font-semibold ${ratioToneClass}`}>
                                                        {ratioDiff >= 0 ? "▲" : "▼"}{Math.abs(ratioDiff).toFixed(2)}
                                                    </span>
                                                    {Number.isFinite(ratioDiffPct) && (
                                                        <span className={`font-num text-xs opacity-70 ${ratioToneClass}`}>
                                                            {ratioDiff >= 0 ? "+" : "-"}{Math.abs(ratioDiffPct).toFixed(2)}%
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="mt-0.5 font-num text-xs text-content-muted">
                                            진입 {Number.isFinite(record.entry_usdt_rate) ? record.entry_usdt_rate.toFixed(2) : "—"}
                                        </div>
                                    </div>
                                </div>

                                {/* 수량 행: FIAT / RISKY · Δ */}
                                <div className="grid grid-cols-2 gap-2.5 border-b border-surface-border px-3.5 py-2.5">
                                    <div className="min-w-0">
                                        <div className="text-xs uppercase tracking-[0.14em] text-content-muted font-medium font-num">FIAT 수량</div>
                                        <div className="mt-0.5 truncate font-num text-xs font-medium text-content">
                                            {Number.isFinite(record.fiat_position_size)
                                                ? Number(record.fiat_position_size).toLocaleString(undefined, { maximumFractionDigits: 4 })
                                                : "—"}
                                        </div>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-xs uppercase tracking-[0.14em] text-content-muted font-medium font-num">RISKY 수량 · Δ</div>
                                        <div className="mt-0.5 truncate font-num text-xs font-medium text-content">
                                            {Number.isFinite(record.risky_position_size)
                                                ? Number(record.risky_position_size).toLocaleString(undefined, { maximumFractionDigits: 4 })
                                                : "—"}
                                            {hasQtyDiff && (
                                                <span className="ml-1.5 text-warning">
                                                    Δ {sizeDiff.toFixed(4)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* FIAT / RISKY 분리 영역 */}
                                <div className="grid grid-cols-2">
                                    <div className="min-w-0 border-r border-surface-border px-3.5 py-3">
                                        <div className="mb-1.5 flex items-center gap-1.5">
                                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
                                            <span className="text-xs font-bold uppercase tracking-[0.1em] text-content-secondary">FIAT</span>
                                        </div>
                                        <div className="truncate font-num text-sm font-semibold text-content">
                                            {krw0Signed(record.fiat_position_value)}
                                        </div>
                                        <div className={`mt-0.5 truncate font-num text-xs ${fiatPnl >= 0 ? "text-gain" : "text-loss"}`}>
                                            {krwSigned(fiatPnl)}
                                            <span className="ml-1 opacity-80">({pctSigned(fiatPnlPct)})</span>
                                        </div>
                                    </div>
                                    <div className="min-w-0 px-3.5 py-3">
                                        <div className="mb-1.5 flex items-center gap-1.5">
                                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-warning" aria-hidden="true" />
                                            <span className="text-xs font-bold uppercase tracking-[0.1em] text-content-secondary">RISKY</span>
                                        </div>
                                        <div className="truncate font-num text-sm font-semibold text-content">
                                            ${formatNumberWithCommaAndDecimal(Math.abs(Number(record.risky_position_value) || 0), 2)}
                                        </div>
                                        <div className={`mt-0.5 truncate font-num text-xs ${riskyPnl >= 0 ? "text-gain" : "text-loss"}`}>
                                            {usdSigned(riskyPnl)}
                                            <span className="ml-1 opacity-70">({krwSigned(riskyPnlKrw)})</span>
                                        </div>
                                        <div className={`mt-0.5 truncate font-num text-xs ${Number.isFinite(riskyPnlPct) ? (riskyPnlPct >= 0 ? "text-gain" : "text-loss") : "text-content-muted"}`}>
                                            {pctSigned(riskyPnlPct)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* default variant (v1 클래식) — 기존 운영 카드 그대로 유지 */}
                    {this.props.variant !== "designed" && finalCombinedData.map((record, index) => {
                        const risk = getRiskLevel(record.risky_profit_loss_percent);
                        const sizeDiff = record.fiat_position_size + record.risky_position_size;
                        const isNormal = record.fiat_position_size === 0 && record.risky_position_size === 0;
                        const isExpanded = expandedTickers.has(record.ticker) || risk.priority === 0;

                        return (
                            <div
                                key={record.ticker}
                                className={`rounded-xl border border-l-4 border-surface-border ${risk.rowBgClass} p-3 sm:p-4 ${risk.borderClass} animate-fade-in${risk.priority === 0 ? ' animate-pulse-soft shadow-glow-loss' : ''}`}
                                style={{ animationDelay: `${Math.min(index * 40, 500)}ms`, animationFillMode: 'backwards' }}
                            >
                                {/* 카드 헤더 - 클릭으로 펼치기/접기 */}
                                <div
                                    className="flex items-center justify-between min-h-[44px] cursor-pointer select-none"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => this.toggleExpand(record.ticker)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.toggleExpand(record.ticker); }}
                                >
                                    <div className="flex min-w-0 items-center gap-1.5">
                                        <span
                                            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${risk.className}`}
                                        >
                                            {record.ticker} · {risk.label}
                                        </span>
                                        {record.in_collateral && (
                                            <span
                                                title={`코인빌리기 담보로 이체된 수량(추정): ${formatNumberWithCommaAndDecimal(record.collateral_qty, 4)} ${record.ticker}`}
                                                aria-label="코인빌리기 담보"
                                                className="rounded-full bg-brand-subtle px-1.5 py-0.5 text-xs font-semibold text-brand-light"
                                            >
                                                담보
                                            </span>
                                        )}
                                        {record.avg_price_overridden && (
                                            <span
                                                title={`업비트 평단 ${formatNumberWithCommaAndDecimal(record.exchange_avg_buy_price, 0)}원 → 보정 ${formatNumberWithCommaAndDecimal(record.fiat_avg_buy_price, 0)}원`}
                                                aria-label="평단 보정"
                                                className="rounded-full bg-brand-subtle px-1.5 py-0.5 text-xs font-semibold text-brand-light"
                                            >
                                                평단 보정
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        {Number.isFinite(record.risky_leverage) && record.risky_leverage > 1 && (
                                            <span className="rounded-md bg-surface-hover px-1.5 py-0.5 text-xs font-semibold text-content-secondary">
                                                {record.risky_leverage}x
                                            </span>
                                        )}
                                        {/* 펼침 방향 화살표 */}
                                        <svg
                                            className={`h-3.5 w-3.5 text-content-muted transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                                            viewBox="0 0 12 12"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            aria-hidden="true"
                                        >
                                            <path d="M2 4l4 4 4-4" />
                                        </svg>
                                    </div>
                                </div>

                                {/* 접힌 상태: 3가지 핵심 지표 요약 */}
                                {!isExpanded && (
                                    <div className="mt-2 grid grid-cols-3 gap-1.5 text-xs overflow-hidden">
                                        <div>
                                            <div className="text-xs text-content-secondary">총 손익금</div>
                                            <div
                                                className={`truncate font-num font-bold ${
                                                    record.total_profit_loss_amount >= 0 ? "text-gain" : "text-loss"
                                                }`}
                                            >
                                                {this.renderPnlIcon(record.total_profit_loss_amount)}
                                                {record.total_profit_loss_amount > 0 ? "+" : ""}₩{formatNumberWithCommaAndDecimal(record.total_profit_loss_amount, 0)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-content-secondary">총 손익률</div>
                                            <div
                                                className={`truncate font-num font-semibold ${
                                                    record.total_profit_loss_ratio != null && Number.isFinite(record.total_profit_loss_ratio)
                                                        ? record.total_profit_loss_ratio >= 0 ? "text-gain" : "text-loss"
                                                        : "text-content-muted"
                                                }`}
                                            >
                                                {this.renderPnlIcon(
                                                    Number.isFinite(record.total_profit_loss_ratio)
                                                        ? record.total_profit_loss_ratio
                                                        : 0
                                                )}
                                                {formatPercent(
                                                    Number.isFinite(record.total_profit_loss_ratio)
                                                        ? record.total_profit_loss_ratio * 100
                                                        : Number.NaN
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-content-secondary">현재 Ratio</div>
                                            <div className={`truncate font-num font-semibold ${
                                                Number.isFinite(record.current_ratio) && Number.isFinite(record.entry_usdt_rate)
                                                    ? record.current_ratio < record.entry_usdt_rate
                                                        ? "text-loss"
                                                        : record.current_ratio > record.entry_usdt_rate
                                                            ? "text-gain"
                                                            : "text-content"
                                                    : "text-content"
                                            }`}>
                                                {Number.isFinite(record.current_ratio)
                                                    ? `₩${formatNumberWithCommaAndDecimal(record.current_ratio, 2)}`
                                                    : "-"}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 펼친 상태: 전체 상세 내용 (grid-rows 트랜지션) */}
                                <div
                                    className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                                        isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                                    }`}
                                >
                                    <div className={isExpanded ? "min-h-0 overflow-visible" : "min-h-0 overflow-hidden"}>
                                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs sm:text-sm">
                                            <div>
                                                <div className="text-xs text-content-secondary">총 손익금</div>
                                                <div
                                                    className={`truncate font-num text-xs font-bold sm:text-base ${
                                                        record.total_profit_loss_amount >= 0
                                                            ? "text-gain"
                                                            : "text-loss"
                                                    }`}
                                                >
                                                    {this.renderPnlIcon(record.total_profit_loss_amount)}
                                                    {record.total_profit_loss_amount > 0 ? "+" : ""}₩{formatNumberWithCommaAndDecimal(record.total_profit_loss_amount, 0)}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-content-secondary">총 손익률</div>
                                                <div
                                                    className={`truncate font-num font-semibold ${
                                                        record.total_profit_loss_ratio != null && Number.isFinite(record.total_profit_loss_ratio)
                                                            ? record.total_profit_loss_ratio >= 0 ? "text-gain" : "text-loss"
                                                            : "text-content-muted"
                                                    }`}
                                                >
                                                    {this.renderPnlIcon(
                                                        Number.isFinite(record.total_profit_loss_ratio)
                                                            ? record.total_profit_loss_ratio
                                                            : 0
                                                    )}
                                                    {formatPercent(
                                                        Number.isFinite(record.total_profit_loss_ratio)
                                                            ? record.total_profit_loss_ratio * 100
                                                            : Number.NaN
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-content-secondary">총 가치</div>
                                                <div className="truncate font-num font-semibold text-content">
                                                    ₩{formatNumberWithCommaAndDecimal(record.total_position_value, 0)}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-content-secondary">현재 Ratio</div>
                                                <div className={`truncate font-num font-semibold ${
                                                    Number.isFinite(record.current_ratio) && Number.isFinite(record.entry_usdt_rate)
                                                        ? record.current_ratio < record.entry_usdt_rate
                                                            ? "text-loss"
                                                            : record.current_ratio > record.entry_usdt_rate
                                                                ? "text-gain"
                                                                : "text-content"
                                                        : "text-content"
                                                }`}>
                                                    {Number.isFinite(record.current_ratio)
                                                        ? `₩${formatNumberWithCommaAndDecimal(record.current_ratio, 2)}`
                                                        : "-"}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-content-secondary">진입 Ratio</div>
                                                <div className="truncate font-num font-semibold text-content">
                                                    {Number.isFinite(record.entry_usdt_rate)
                                                        ? `₩${formatNumberWithCommaAndDecimal(record.entry_usdt_rate, 2)}`
                                                        : "-"}
                                                </div>
                                            </div>
                                        </div>

                                        {!isNormal && (() => {
                                            const diffValue = Math.abs(sizeDiff) * (record.fiat_current_price || 0);
                                            const isHighDiff = diffValue >= 10000;
                                            const isPerfectHedge = sizeDiff === 0;

                                            const stateClass = isPerfectHedge
                                                ? "bg-surface-hover border-l-2 border-gain"
                                                : isHighDiff
                                                    ? "bg-loss-subtle border-l-2 border-loss"
                                                    : "bg-gain-subtle border-l-2 border-gain-dark";

                                            const labelClass = isPerfectHedge
                                                ? "text-content-secondary"
                                                : isHighDiff ? "text-content-muted" : "text-content-muted";

                                            const valueClass = isPerfectHedge
                                                ? "text-content"
                                                : isHighDiff ? "text-loss" : "text-gain";

                                            return (
                                                <div className={`mt-3 rounded-lg p-2 text-xs ${stateClass} animate-fade-in`}>
                                                    <div className="grid grid-cols-3 gap-2 text-center">
                                                        <div>
                                                            <div className={labelClass}>Fiat</div>
                                                            <div className={`font-num font-semibold ${valueClass}`}>
                                                                {formatNumberWithCommaAndDecimal(record.fiat_position_size, 4)}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className={labelClass}>Risky</div>
                                                            <div className={`font-num font-semibold ${valueClass}`}>
                                                                {formatNumberWithCommaAndDecimal(record.risky_position_size, 4)}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className={labelClass}>차이</div>
                                                            <div className={`font-num font-semibold ${valueClass}`}>
                                                                {formatNumberWithCommaAndDecimal(sizeDiff, 4)}
                                                            </div>
                                                            {!isPerfectHedge && (
                                                                <div className={`text-xs ${isHighDiff ? "text-loss" : "text-gain"}`}>
                                                                    (₩{formatNumberWithCommaAndDecimal(diffValue, 0)})
                                                                </div>
                                                            )}
                                                            {isPerfectHedge && (
                                                                <div className="text-xs text-content-muted">정합</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-surface-border pt-3">
                                            <div className="min-w-0">
                                                <div className="text-xs font-semibold uppercase text-content-secondary">Fiat</div>
                                                <div className="mt-1 space-y-1 text-xs sm:text-sm">
                                                    <div className="flex justify-between gap-1">
                                                        <span className="shrink-0 text-content-secondary">가치</span>
                                                        <span className={`truncate font-num ${record.fiat_position_value === 0 ? "text-loss" : "text-content"}`}>
                                                            ₩{formatNumberWithCommaAndDecimal(record.fiat_position_value, 0)}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between gap-1">
                                                        <span className="shrink-0 text-content-secondary">손익</span>
                                                        <span
                                                            className={`truncate font-num ${
                                                                record.fiat_profit_loss_amount > 0
                                                                    ? "text-gain"
                                                                    : record.fiat_profit_loss_amount < 0
                                                                    ? "text-loss"
                                                                    : "text-content"
                                                            }`}
                                                        >
                                                            {this.renderPnlIcon(record.fiat_profit_loss_amount)}
                                                            {record.fiat_profit_loss_amount > 0 ? "+" : ""}₩{formatNumberWithCommaAndDecimal(record.fiat_profit_loss_amount, 0)}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between gap-1">
                                                        <span className="shrink-0 text-content-secondary">손익률</span>
                                                        <span
                                                            className={`truncate font-num ${
                                                                record.fiat_profit_loss_amount > 0
                                                                    ? "text-gain"
                                                                    : record.fiat_profit_loss_amount < 0
                                                                    ? "text-loss"
                                                                    : "text-content"
                                                            }`}
                                                        >
                                                            {formatPercent(
                                                                record.fiat_position_value
                                                                    ? (record.fiat_profit_loss_amount / record.fiat_position_value) * 100
                                                                    : Number.NaN
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-xs font-semibold uppercase text-content-secondary">Risky</div>
                                                <div className="mt-1 space-y-1 text-xs sm:text-sm">
                                                    <div>
                                                        <div className="flex justify-between gap-1">
                                                            <span className="shrink-0 text-content-secondary">가치</span>
                                                            <span className={`truncate font-num ${record.risky_position_value === 0 ? "text-loss" : "text-content"}`}>
                                                                ${formatNumberWithCommaAndDecimal(Math.abs(record.risky_position_value), 2)}
                                                            </span>
                                                        </div>
                                                        <div className="truncate text-right font-num text-xs text-content-muted">
                                                            (₩{formatNumberWithCommaAndDecimal(Math.abs(record.risky_position_value) * exchangeRate, 0)})
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="flex justify-between gap-1">
                                                            <span className="shrink-0 text-content-secondary">손익</span>
                                                            <span
                                                                className={`truncate font-num ${
                                                                    record.risky_profit_loss_amount > 0
                                                                        ? "text-gain"
                                                                        : record.risky_profit_loss_amount < 0
                                                                        ? "text-loss"
                                                                        : "text-content"
                                                                }`}
                                                            >
                                                                {this.renderPnlIcon(record.risky_profit_loss_amount)}
                                                                {record.risky_profit_loss_amount > 0 ? "+" : ""}${formatNumberWithCommaAndDecimal(record.risky_profit_loss_amount, 2)}
                                                            </span>
                                                        </div>
                                                        <div className={`truncate text-right font-num text-xs ${
                                                            record.risky_profit_loss_amount > 0
                                                                ? "text-gain"
                                                                : record.risky_profit_loss_amount < 0
                                                                ? "text-loss"
                                                                : "text-content-muted"
                                                        }`}>
                                                            (₩{formatNumberWithCommaAndDecimal(record.risky_profit_loss_amount * exchangeRate, 0)})
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-between gap-1">
                                                        <span className="shrink-0 text-content-secondary">손익률</span>
                                                        <span
                                                            className={`truncate font-num ${
                                                                record.risky_profit_loss_amount > 0
                                                                    ? "text-gain"
                                                                    : record.risky_profit_loss_amount < 0
                                                                    ? "text-loss"
                                                                    : "text-content"
                                                            }`}
                                                        >
                                                            {formatPercent(record.risky_profit_loss_percent)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {finalCombinedData.length === 0 && (
                        <EmptyState
                            title="표시할 포지션이 없습니다"
                            description="필터 조건을 변경하거나 데이터가 수집될 때까지 기다려주세요."
                        />
                    )}
                </div>
            </div>
        );
    }
}

export default PositionTable;
